import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import CodeScannerComponent from './CodeScanner';

/**
 * Input numérico com UX de "zero some ao focar / volta ao sair vazio".
 * Mantém um texto local para permitir o estado intermediário vazio sem
 * precisar refletir undefined no state externo (que continua sempre numérico).
 */
const NumeroInput = ({ value, onChange, ...inputProps }) => {
    const [texto, setTexto] = useState(value === 0 || value == null ? '0' : String(value));

    // Sincroniza com mudanças externas (reidratar edição, recálculo etc.)
    useEffect(() => {
        const numAtual = parseFloat(texto);
        const numProp = Number(value);
        if (numAtual !== numProp && !(isNaN(numAtual) && numProp === 0)) {
            setTexto(numProp === 0 ? '0' : String(numProp));
        }
    }, [value]);

    const handleFocus = (e) => {
        if (parseFloat(texto) === 0) setTexto('');
        // Seleciona o conteúdo restante para edição rápida
        requestAnimationFrame(() => { try { e.target.select(); } catch {} });
    };

    const handleBlur = () => {
        if (texto === '' || texto === '-' || isNaN(parseFloat(texto))) {
            setTexto('0');
            onChange(0);
        }
    };

    const handleChange = (e) => {
        const novo = e.target.value;
        setTexto(novo);
        if (novo === '' || novo === '-') return;     // estado intermediário
        const num = parseFloat(novo);
        if (!isNaN(num)) onChange(num);
    };

    return (
        <input
            type="number"
            {...inputProps}
            value={texto}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
        />
    );
};

/*
 * Tela de orçamento — espelha o padrão do Inventario (scanner + tabela editável),
 * mas grava na DecisionAPI: tchai-api.2.0 só atua como ponte HTTP.
 *
 * Modos:
 *   - vendaId vazio  → criar novo (POST  /comercial/orcamento/salvar)
 *   - vendaId set    → editar     (PUT   /comercial/orcamento/salvar/:venda)
 *
 * Cliente: usuário preenche nome/CPF/email/telefone. A DecisionAPI tenta achar
 * pelo CPF ou email; se não achar, cria um novo via CLIENTE_I. Sem dropdown.
 */

const normalizar = (s) => String(s ?? '').replace(/^0+/, '');
// Para exibição: corta zeros à esquerda do código de barras (puramente visual).
const fmtCodBarras = (cb) => {
    const s = String(cb ?? '').trim();
    if (!s) return '';
    const stripped = s.replace(/^0+/, '');
    return stripped || '0';
};
const fmtMoeda = (n) =>
    Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Normaliza valor vindo do backend (BLOB às vezes vem como objeto)
const asTextoSeguro = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return String(v);
    return ''; // BLOB handle, Buffer não-stringificável, etc.
};

const LinhaItem = ({ item, onQtdChange, onPrecoChange, onDescontoChange, onRemover }) => {
    const subtotal = (item.quantidade || 0) * (item.preco || 0) - (item.desconto || 0);
    return (
        <tr>
            <td>
                <div className="fw-bold">
                    {item.produto.DESCRICAO}
                    {item.produto.PARAMETRO1 && (
                        <span className="text-muted ms-1">
                            ({item.produto.PARAMETRO1} {item.produto.PARAMETRO2})
                        </span>
                    )}
                </div>
                <small className="text-muted">Cód: {item.produto.PRODUTO}</small>
            </td>
            <td><span className="font-monospace">{fmtCodBarras(item.produto.CODIGOBARRAS)}</span></td>
            <td style={{ width: 110 }}>
                {/* Aceita decimais (até 3) para produtos vendidos por kg/metro */}
                <NumeroInput
                    className="form-control form-control-sm"
                    value={item.quantidade}
                    min="0"
                    step="0.001"
                    onChange={(v) => onQtdChange(item.produto.PRODUTO, item.produto.GRADE, v)}
                />
            </td>
            <td style={{ width: 130 }}>
                <NumeroInput
                    className="form-control form-control-sm"
                    value={item.preco}
                    min="0"
                    step="0.01"
                    onChange={(v) => onPrecoChange(item.produto.PRODUTO, item.produto.GRADE, v)}
                />
            </td>
            <td style={{ width: 120 }}>
                <NumeroInput
                    className="form-control form-control-sm"
                    value={item.desconto || 0}
                    min="0"
                    step="0.01"
                    onChange={(v) => onDescontoChange(item.produto.PRODUTO, item.produto.GRADE, v)}
                />
            </td>
            <td className="text-end fw-bold">
                {fmtMoeda(subtotal)}
            </td>
            <td className="text-end" style={{ width: 60 }}>
                <button
                    className="btn btn-danger btn-sm"
                    onClick={() => onRemover(item.produto.PRODUTO, item.produto.GRADE)}
                    title="Remover item"
                >
                    <i className="bi bi-trash"></i>
                </button>
            </td>
        </tr>
    );
};

const OrcamentoComponent = ({ vendaId, vendedorPadrao, opcoes, catalogo, orcamento }) => {
    const isEdicao = Boolean(vendaId);
    const header = orcamento?.header || null;
    const itensCarregados = orcamento?.itens || [];

    // Os selects do React comparam `value` (string) com cada `<option value>` (string).
    // Como os IDs vêm como number do Firebird, normalizamos tudo para string aqui.
    const valorOuVazio = (v) => (v === null || v === undefined || v === '') ? '' : String(v);

    const [filial,       setFilial]       = useState(valorOuVazio(header?.FILIAL));
    const [vendedor,     setVendedor]     = useState(
        valorOuVazio(header?.VENDEDOR) || valorOuVazio(vendedorPadrao)
    );
    const [almoxarifado, setAlmoxarifado] = useState(
        valorOuVazio(itensCarregados[0]?.ALMOXARIFADO)
    );

    const [clienteId, setClienteId] = useState(valorOuVazio(header?.CLIENTE));
    const [nome,      setNome]      = useState(header?.CLIENTE_NOME      || '');
    const [documento, setDocumento] = useState(header?.CLIENTE_DOCUMENTO || '');
    const [email,     setEmail]     = useState(header?.CLIENTE_EMAIL     || '');
    const [telefone,  setTelefone]  = useState(header?.CLIENTE_TELEFONE  || '');

    const [desconto, setDesconto] = useState(header?.VALORDESCONTO || 0);
    const [frete,    setFrete]    = useState(header?.VALORFRETE    || 0);
    const [observacao, setObservacao] = useState(() => {
        const obs = asTextoSeguro(header?.OBSERVACOES);
        // remove o prefixo automático "Orçamento manual #N | " ao exibir
        return obs.replace(/^Orçamento manual #\d+\s*\|?\s*/i, '');
    });

    const [itens, setItens] = useState(() => {
        if (!isEdicao) return [];
        return itensCarregados.map((vi) => {
            // Usa GRADE da própria VENDAITEM e enriquece os demais campos
            // pelo catálogo (parâmetros, código de barras).
            const matchCat = catalogo.find(
                (c) => Number(c.PRODUTO) === Number(vi.PRODUTO)
                    && Number(c.GRADE)   === Number(vi.GRADE)
            ) || catalogo.find(
                (c) => Number(c.PRODUTO) === Number(vi.PRODUTO)
            );
            return {
                produto: {
                    PRODUTO: vi.PRODUTO,
                    GRADE: vi.GRADE ?? matchCat?.GRADE ?? 0,
                    DESCRICAO: vi.DESCRICAO || matchCat?.DESCRICAO || '',
                    PARAMETRO1: matchCat?.PARAMETRO1 || '',
                    PARAMETRO2: matchCat?.PARAMETRO2 || '',
                    CODIGOBARRAS: matchCat?.CODIGOBARRAS || '',
                    PRECO: matchCat?.PRECO ?? vi.PRECO,
                },
                quantidade: Number(vi.QUANTIDADE) || 1,
                preco: Number(vi.PRECO) || 0,
                desconto: Number(vi.VALORDESCONTO) || 0,
            };
        });
    });

    const [salvando, setSalvando] = useState(false);
    const [busca, setBusca] = useState('');
    const [modalAberto, setModalAberto] = useState(false);

    /* -------- Resultados do modal (filtrados em tempo real) -------- */
    const resultadosBusca = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return catalogo.slice(0, 30);
        const termoNorm = normalizar(termo);
        return catalogo
            .filter((p) =>
                normalizar(p.CODIGOBARRAS) === termoNorm
                || normalizar(p.CODIGOBARRAS).startsWith(termoNorm)
                || String(p.PRODUTO) === termo
                || String(p.PRODUTO).startsWith(termo)
                || (p.DESCRICAO || '').toLowerCase().includes(termo)
            )
            .slice(0, 50);
    }, [busca, catalogo]);

    // Total bruto dos itens, já subtraindo o desconto de cada um.
    // Desconto do header e frete são aplicados na exibição final separadamente.
    const total = useMemo(
        () => itens.reduce(
            (acc, it) => acc + (it.quantidade || 0) * (it.preco || 0) - (it.desconto || 0),
            0,
        ),
        [itens]
    );

    /* -------- Manipulação de itens -------- */

    const adicionarItem = (produto) => {
        setItens((prev) => {
            const idx = prev.findIndex(
                (it) => Number(it.produto.PRODUTO) === Number(produto.PRODUTO)
                     && Number(it.produto.GRADE)   === Number(produto.GRADE)
            );
            if (idx !== -1) {
                const novo = { ...prev[idx], quantidade: prev[idx].quantidade + 1 };
                const lista = [...prev];
                lista.splice(idx, 1);
                return [novo, ...lista];
            }
            return [{
                produto,
                quantidade: 1,
                preco: Number(produto.PRECO) || 0,
                desconto: 0,
            }, ...prev];
        });
    };

    const handleScanResult = (codigo) => {
        const codNorm = normalizar(codigo);
        const encontrado = catalogo.find(
            (p) => normalizar(p.CODIGOBARRAS) === codNorm
        );
        if (encontrado) {
            adicionarItem(encontrado);
        } else {
            alert(`Produto com código "${codigo}" não encontrado.`);
        }
    };

    const abrirModalBusca = () => { setBusca(''); setModalAberto(true); };
    const fecharModalBusca = () => { setModalAberto(false); };

    const selecionarProdutoDoModal = (produto) => {
        adicionarItem(produto);
        fecharModalBusca();
    };

    const handleQtdChange = (produtoId, grade, novaQtd) => {
        setItens((prev) => prev.map((it) =>
            Number(it.produto.PRODUTO) === Number(produtoId)
            && Number(it.produto.GRADE) === Number(grade)
                ? { ...it, quantidade: novaQtd }
                : it
        ));
    };

    const handlePrecoChange = (produtoId, grade, novoPreco) => {
        setItens((prev) => prev.map((it) =>
            Number(it.produto.PRODUTO) === Number(produtoId)
            && Number(it.produto.GRADE) === Number(grade)
                ? { ...it, preco: novoPreco }
                : it
        ));
    };

    const handleDescontoChange = (produtoId, grade, novoDesconto) => {
        setItens((prev) => prev.map((it) =>
            Number(it.produto.PRODUTO) === Number(produtoId)
            && Number(it.produto.GRADE) === Number(grade)
                ? { ...it, desconto: novoDesconto }
                : it
        ));
    };

    const handleRemover = (produtoId, grade) => {
        setItens((prev) => prev.filter((it) =>
            !(Number(it.produto.PRODUTO) === Number(produtoId)
              && Number(it.produto.GRADE) === Number(grade))
        ));
    };

    /* -------- Submissão -------- */

    const validar = () => {
        if (!filial)        return 'Selecione a filial.';
        if (!vendedor)      return 'Selecione o vendedor.';
        if (!almoxarifado)  return 'Selecione o almoxarifado.';
        if (!nome.trim() && !clienteId) return 'Informe o nome do cliente.';
        if (itens.length === 0) return 'Adicione ao menos um item.';
        for (const it of itens) {
            if (!(Number(it.quantidade) > 0)) return 'Quantidade inválida em algum item.';
            if (it.preco < 0) return 'Preço inválido em algum item.';
            if ((it.desconto || 0) < 0) return 'Desconto inválido em algum item.';
            const subtotal = it.quantidade * it.preco - (it.desconto || 0);
            if (subtotal < 0) return 'Desconto não pode ser maior que o valor do item.';
        }
        return null;
    };

    const handleSalvar = async () => {
        const erro = validar();
        if (erro) {
            alert(erro);
            return;
        }
        setSalvando(true);

        const payload = {
            filial: Number(filial),
            vendedor: Number(vendedor),
            almoxarifado: Number(almoxarifado),
            cliente: {
                cliente_id: clienteId ? Number(clienteId) : null,
                name: nome,
                identification: documento,
                email,
                phone: telefone,
            },
            items: itens.map((it) => ({
                local_id: Number(it.produto.PRODUTO),
                name: it.produto.DESCRICAO,
                quantity: Number(it.quantidade),
                price: Number(it.preco),
                discount: Number(it.desconto) || 0,
            })),
            total,
            discount: Number(desconto) || 0,
            shipping: Number(frete) || 0,
            observacao,
        };

        try {
            if (isEdicao) {
                const resp = await axios.put(`/comercial/orcamento/salvar/${vendaId}`, payload);
                alert(`Orçamento #${vendaId} atualizado com sucesso.`);
            } else {
                const resp = await axios.post('/comercial/orcamento/salvar', payload);
                alert(`Orçamento #${resp.data.venda} criado com sucesso.`);
            }
            window.location.href = '/comercial/orcamento';
        } catch (err) {
            alert('Erro ao salvar orçamento: ' + (err.response?.data?.message || err.message));
        } finally {
            setSalvando(false);
        }
    };

    /* -------- Render -------- */

    return (
        <>
            <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <label className="form-label small fw-bold text-secondary">Filial</label>
                    <select className="form-select" value={filial} onChange={(e) => setFilial(e.target.value)} required>
                        <option value="">Selecione...</option>
                        {opcoes.filiais.map((f) => (
                            <option key={f.FILIAL} value={f.FILIAL}>{f.FILIAL} - {f.NOME}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label small fw-bold text-secondary">Vendedor</label>
                    <select className="form-select" value={vendedor} onChange={(e) => setVendedor(e.target.value)} required>
                        <option value="">Selecione...</option>
                        {opcoes.usuarios.map((u) => (
                            <option key={u.USUARIO} value={u.USUARIO}>{u.USUARIO} - {u.NOME}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label small fw-bold text-secondary">Almoxarifado</label>
                    <select className="form-select" value={almoxarifado} onChange={(e) => setAlmoxarifado(e.target.value)} required>
                        <option value="">Selecione...</option>
                        {opcoes.almoxarifados.map((a) => (
                            <option key={a.ALMOXARIFADO} value={a.ALMOXARIFADO}>{a.ALMOXARIFADO} - {a.NOME}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light">
                    <h6 className="mb-0"><i className="bi bi-person-vcard me-1"></i> Cliente</h6>
                </div>
                <div className="card-body">
                    {isEdicao && clienteId && (
                        <div className="alert alert-info py-2 mb-3 small">
                            Editando cliente cadastrado (CLIENTE {clienteId}).
                        </div>
                    )}
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label small fw-bold text-secondary">Nome</label>
                            <input type="text" className="form-control" value={nome} onChange={(e) => setNome(e.target.value)} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-secondary">CPF/CNPJ</label>
                            <input type="text" className="form-control" value={documento} onChange={(e) => setDocumento(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-secondary">Telefone</label>
                            <input type="text" className="form-control" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small fw-bold text-secondary">E-mail</label>
                            <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>
                    </div>
                    <p className="text-muted small mb-0 mt-2">
                        <i className="bi bi-info-circle me-1"></i>
                        Se o CPF/CNPJ ou e-mail já existir, o cliente é reaproveitado. Caso contrário, é cadastrado automaticamente.
                    </p>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-5">
                    <div className="card shadow-sm">
                        <div className="card-header"><h6 className="mb-0">Leitor de Código de Barras</h6></div>
                        <div className="card-body no-margin">
                            <CodeScannerComponent onScanResult={handleScanResult} />
                        </div>
                    </div>
                    <div className="mt-3 d-grid">
                        <button type="button" className="btn btn-outline-primary" onClick={abrirModalBusca}>
                            <i className="bi bi-search me-1"></i> Buscar produto manualmente
                        </button>
                    </div>
                </div>

                <div className="col-lg-7">
                    <div className="card shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Itens do orçamento</h6>
                            <span className="badge bg-secondary">{itens.length} itens</span>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-striped table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Produto</th>
                                            <th>Cód. Barras</th>
                                            <th>Qtd</th>
                                            <th>Preço</th>
                                            <th>Desconto</th>
                                            <th className="text-end">Subtotal</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itens.length > 0 ? itens.map((item) => (
                                            <LinhaItem
                                                key={`${item.produto.PRODUTO}-${item.produto.GRADE}`}
                                                item={item}
                                                onQtdChange={handleQtdChange}
                                                onPrecoChange={handlePrecoChange}
                                                onDescontoChange={handleDescontoChange}
                                                onRemover={handleRemover}
                                            />
                                        )) : (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">
                                                    Escaneie ou busque produtos para adicionar.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm mt-3">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-secondary">Desconto (R$)</label>
                            <NumeroInput min="0" step="0.01" className="form-control" value={desconto} onChange={setDesconto} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-secondary">Frete (R$)</label>
                            <NumeroInput min="0" step="0.01" className="form-control" value={frete} onChange={setFrete} />
                        </div>
                        <div className="col-md-6 text-end">
                            <div className="text-muted small">Total do orçamento</div>
                            <div className="display-6 fw-bold text-success">
                                {fmtMoeda(total - (Number(desconto) || 0) + (Number(frete) || 0))}
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label small fw-bold text-secondary">Observações</label>
                            <textarea className="form-control" rows="2" value={observacao} onChange={(e) => setObservacao(e.target.value)}></textarea>
                        </div>
                    </div>
                </div>
            </div>

            <div className="my-4 d-flex gap-2">
                <button
                    className="btn btn-success flex-grow-1 p-3 fw-bold"
                    onClick={handleSalvar}
                    disabled={salvando}
                >
                    {salvando ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Salvando...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-save me-2"></i>
                            {isEdicao ? 'Atualizar orçamento' : 'Salvar orçamento'}
                        </>
                    )}
                </button>
                {isEdicao && (
                    <button
                        type="button"
                        className="btn btn-outline-primary p-3 fw-bold"
                        onClick={() => window.open(`/comercial/orcamento/imprimir/${vendaId}`, '_blank')}
                        title="Abre nova aba com o orçamento formatado para impressão"
                    >
                        <i className="bi bi-printer me-2"></i>
                        Imprimir orçamento
                    </button>
                )}
            </div>

            {modalAberto && (
                <>
                    <div className="modal d-block" tabIndex="-1" role="dialog"
                         style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={fecharModalBusca}>
                        <div className="modal-dialog modal-lg modal-dialog-scrollable"
                             role="document" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className="bi bi-search me-2"></i>Buscar produto
                                    </h5>
                                    <button type="button" className="btn-close" onClick={fecharModalBusca}></button>
                                </div>
                                <div className="modal-body">
                                    <input
                                        type="text"
                                        autoFocus
                                        className="form-control mb-3"
                                        placeholder="Digite código, código de barras ou descrição..."
                                        value={busca}
                                        onChange={(e) => setBusca(e.target.value)}
                                    />
                                    <div className="text-muted small mb-2">
                                        {resultadosBusca.length} resultado(s)
                                    </div>
                                    <div className="list-group">
                                        {resultadosBusca.length === 0 ? (
                                            <div className="text-center text-muted py-4">
                                                Nenhum produto encontrado.
                                            </div>
                                        ) : resultadosBusca.map((p) => (
                                            <button
                                                key={`${p.PRODUTO}-${p.GRADE}-${p.CODIGOBARRAS}`}
                                                type="button"
                                                className="list-group-item list-group-item-action"
                                                onClick={() => selecionarProdutoDoModal(p)}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="fw-bold">
                                                            {p.DESCRICAO}
                                                            {p.PARAMETRO1 && (
                                                                <span className="text-muted ms-1">
                                                                    ({p.PARAMETRO1} {p.PARAMETRO2})
                                                                </span>
                                                            )}
                                                        </div>
                                                        <small className="text-muted">
                                                            Cód: <strong>{p.PRODUTO}</strong>
                                                            {p.CODIGOBARRAS && (
                                                                <> · Barras: <span className="font-monospace">{fmtCodBarras(p.CODIGOBARRAS)}</span></>
                                                            )}
                                                        </small>
                                                    </div>
                                                    <span className="badge bg-success rounded-pill">
                                                        {fmtMoeda(p.PRECO)}
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={fecharModalBusca}>
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default OrcamentoComponent;
